/* eslint-disable max-lines */
/* eslint-disable no-console */
import { AdditionalPageOptions } from '../interfaces/additionalPageOptions';

type Schema<T> = { [Property in keyof T]: T[Property] extends string ? Function : Schema<T[Property]> };
type RecordKey = string|number|symbol;
type SchemaRecord<T> = { [K in RecordKey]: Schema<T[keyof T]> };

const RECORD_MARKER = '_record_' as RecordKey;
const isSchemaRecord = <T>(object: object): object is SchemaRecord<T> =>
    Boolean(Object.keys(object).length) && Object.keys(object)[0] === RECORD_MARKER;
const isObject = (value: unknown): value is Object => value !== null && typeof value === 'object';

const validateRecord = <T>(recordValue: T, schema: Schema<T> | Function, parentKey: string) => {
    for (const [key, value] of Object.entries(recordValue as object)) {
        if (typeof schema === 'function') {
            const isValid: boolean = schema(value, key, parentKey);

            if (!isValid) {
                return false;
            }

            continue;
        }

        // eslint-disable-next-line no-use-before-define
        const isValid = validate(value, schema, parentKey);

        if (!isValid) {
            console.warn(`One of the value of the "${parentKey}.${key}" has invalid type!`);
            return false;
        }
    }

    return true;
};

const validate = <T, K extends keyof T>(objectOrString: T, schema: Schema<T>, parentKey: string) => {
    for (const [key, validator] of Object.entries(schema)) {
        const current = objectOrString[key as keyof T];

        if (typeof validator === 'function') {
            const isValid = validator(current, key, parentKey);

            if (!isValid) {
                return false;
            }
        }

        if (isObject(objectOrString) && isObject(validator)) {
            if (!(key in (objectOrString))) {
                console.warn(`Property "${parentKey}.${key}" is required!`);
                return false;
            }

            const isValid = isSchemaRecord<T>(validator)
                ? validateRecord(current, validator[RECORD_MARKER], key)
                : validate(current, validator as Schema<T[K]>, key);

            if (!isValid) {
                return false;
            }
        }
    }

    return true;
};

const requiredString = (value: string, key: string, parentKey: string) => {
    if (!value) {
        console.warn(`Property "${parentKey}.${key}" is required!`);
        return false;
    }

    if (typeof value !== 'string') {
        console.warn(`Property "${parentKey}.${key}" is not string!`);
        return false;
    }

    return true;
};

/** Only for testing! */
export const MOCK_SCHEMA: Schema<any> = {
    key: requiredString,
    object: {
        nestedObject: {
            nestedKey: requiredString,
        },
    },
    recordWithString: {
        [RECORD_MARKER]: requiredString,
    },
    recordWithObject: {
        [RECORD_MARKER]: {
            key: requiredString,
        },
    },
};

const CMS_SCHEMA: Schema<AdditionalPageOptions> = {
    selected: {
        tariffId: requiredString,
    },
    tariffIds: {
        [RECORD_MARKER]: requiredString,
    },
    lifeCycleIds: {
        [RECORD_MARKER]: requiredString,
    },
    preFiberBannerId: requiredString,
    homesPassedIds: {
        markerBHS: requiredString,
        markerBKV: requiredString,
    },
    tariffBenefits: {
        [RECORD_MARKER]: {
            headline: requiredString,
            iconName: requiredString,
            tariffs: {
                [RECORD_MARKER]: requiredString,
            },
        },
    },
    regioFeeBenefit: {
        minimumChargeEuro: requiredString,
        headline: requiredString,
        iconName: requiredString,
        tariffs: {
            [RECORD_MARKER]: requiredString,
        },
    },
    footnoteTariffItem: {
        headlineText: requiredString,
        sections: {
            [RECORD_MARKER]: requiredString,
        },
        discountDetail: requiredString,
        regioFeeDetail: requiredString,
        wechselgarantieDetail: {
            headline: requiredString,
            content: requiredString,
        },
        excludeDevices: requiredString,
    },
    avc: {
        addressForm: requiredString,
        resultLayer: requiredString,
    },
    captcha: {
        [RECORD_MARKER]: requiredString,
    },
    avcResult: {
        gigacube: {
            link: requiredString,
            text: requiredString,
            button: requiredString,
        },
        dsl: {
            text: requiredString,
            button: requiredString,
        },
        cable: {
            text: requiredString,
            button: requiredString,
        },
        preFiber: {
            available: {
                text: requiredString,
                regioFee: {
                    text: requiredString,
                    tooltip: {
                        text: requiredString,
                        headline: requiredString,
                    },
                    button: requiredString,
                },
                fastest: requiredString,
                buttonRedirect: requiredString,
            },
            notAvailable: {
                text: requiredString,
            },
            slowerAvailable: {
                text: requiredString,
                slower: requiredString,
            },
        },
        fiber: {
            headline: requiredString,
            subline: requiredString,
            button: requiredString,
        },
        homesPassed: {
            bhs: {
                headline: requiredString,
                subline: requiredString,
            },
            bkv: {
                headline: requiredString,
                subline: requiredString,
                button: {
                    regioCharge5: requiredString,
                    regioCharge5Plus: requiredString,
                },
            },
        },
    },
    tariffDetails: {
        sublineText: requiredString,
        tooltip: {
            text: requiredString,
        },
    },
    offerSummaryCard: {
        notAvailable: {
            headline: requiredString,
            subline: requiredString,
        },
        fiberAvailable: {
            headline: requiredString,
            subline: requiredString,
        },
        homesPassedBHS: {
            headline: requiredString,
            subline: requiredString,
        },
        legalText: {
            durationLine: requiredString,
            installationPriceLine: {
                text: requiredString,
                tooltip: {
                    text: requiredString,
                    element: requiredString,
                },
            },
        },
        priceText: {
            monthly: requiredString,
            regularPrice: requiredString,
            connectionPrice: requiredString,
        },
        button: {
            avc: requiredString,
            option: requiredString,
            product: requiredString,
            fiber: requiredString,
            hotline: {
                text: requiredString,
                link: requiredString,
            },
        },
    },
    visibleDiscountMetacodes: {
        stairway: requiredString,
        provisionFee: requiredString,
        installationFee: requiredString,
        provisionFeeDiscount: requiredString,
        routerDiscount: requiredString,
    },
    avcTooltip: {
        text: requiredString,
    },
    priceListLink: requiredString,
    cableIdentifier: requiredString,
    hotlineNumber: requiredString,
};

const isCmsConfigValid = (schema: Schema<unknown> = CMS_SCHEMA) => {
    if (!window.additionalPageOptions) {
        console.warn('AdditionalPageOptions is missing!');
        return false;
    }

    return validate(window.additionalPageOptions, schema, 'additionalPageOptions');
};

export default isCmsConfigValid;
